/**
 * Core Hypothesis Slide
 * Trust is the New Currency · Satisfying User Personas is the New Standard
 */

import React from 'react';
import styled from 'styled-components';
import { colors } from '@config/colors';
import { typography } from '@config/typography';
import { spacing } from '@config/spacing';
import {
  SlideBase,
  SlideContent,
  SlideHeader,
  SlideAccentGlow,
  SlideAccentGlowBottom,
  DarkAccentBox,
  CardRow,
  SlideLabel,
  DarkCardBody,
  DarkBulletList,
  DarkBulletItem,
} from '@components/press/pitch-deck/slide-primitives';

/* -------------------------------------------------- */
/* Local styled components                            */
/* -------------------------------------------------- */

const ThesisCard = styled(DarkAccentBox)`
  flex: 1;
  min-width: 260px;
  display: flex;
  flex-direction: column;
  gap: ${spacing.lg};
`;

const ThesisHeading = styled.h3`
  font-family: ${typography.fontFamily.heading};
  font-size: clamp(${typography.fontSize.lg}, 2.2vw, ${typography.fontSize['2xl']});
  font-weight: ${typography.fontWeight.semibold};
  color: #fff;
  margin: 0;
  line-height: ${typography.lineHeight.tight};
`;

/* -------------------------------------------------- */
/* Component                                          */
/* -------------------------------------------------- */

export const CoreHypothesisSlide: React.FC = () => (
  <SlideBase>
    <SlideAccentGlow />
    <SlideAccentGlowBottom />
    <SlideContent>
      <SlideHeader
        title="Core Hypothesis"
        subtitle="Two forces reshaping how families choose technology"
      />

      <CardRow $gap={spacing.xl} $mdGap={spacing['2xl']}>
        {/* ---- Thesis 1 ---- */}
        <ThesisCard $accent={colors.accent.teal.exxtraDark}>
          <SlideLabel $color={colors.accent.teal.extraLight}>
            Thesis 1
          </SlideLabel>
          <ThesisHeading>Trust Is the New Currency</ThesisHeading>
          <DarkCardBody>
            Families will only delegate high-stakes decisions&mdash;childcare,
            finances, health&mdash;to software they deeply trust. The winner
            earns trust through transparency, accuracy, and
            never monetizing the family&apos;s data against them.
          </DarkCardBody>
          <DarkBulletList>
            <DarkBulletItem>
              Moms already vet every recommendation from friends&mdash;AI must
              clear the same bar
            </DarkBulletItem>
            <DarkBulletItem>
              Trust compounds: each correct answer raises the ceiling on what
              families will delegate
            </DarkBulletItem>
            <DarkBulletItem>
              First platform to earn deep trust locks in a durable, switchable-cost moat
            </DarkBulletItem>
          </DarkBulletList>
        </ThesisCard>

        {/* ---- Thesis 2 ---- */}
        <ThesisCard $accent={colors.accent.purple.exxtraDark}>
          <SlideLabel $color={colors.accent.purple.extraLight}>
            Thesis 2
          </SlideLabel>
          <ThesisHeading>
            Satisfying User Personas Is the New Standard
          </ThesisHeading>
          <DarkCardBody>
            Households are not one user&mdash;they are a web of personas
            (planner, chauffeur, nurse, chef, CFO). The platform that serves
            the full persona graph will be one of few winners.
          </DarkCardBody>
          <DarkBulletList>
            <DarkBulletItem>
              Point solutions serve one persona; families juggle dozens daily
            </DarkBulletItem>
            <DarkBulletItem>
              Cross-persona context (the budget affects the meal plan affects
              the grocery run) is the unlock
            </DarkBulletItem>
            <DarkBulletItem>
              Few companies will build the breadth + depth required&mdash;winner-take-most
              dynamics
            </DarkBulletItem>
          </DarkBulletList>
        </ThesisCard>
      </CardRow>
    </SlideContent>
  </SlideBase>
);
